import React from "react";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { isUserAuthorized, getCurrentTheme } from "./store/selectors";
import { checkToken, fetchUserProfile, loadInitialData } from "./actions/authActions";
import withClearCache from "./ClearCache";
import { ConfigProvider, theme } from "antd";
import { THEME } from "./constants";
import es_ES from "antd/es/locale/es_ES";
import { getCookie, setCookie } from "./utils";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const App = () => {
	const dispatch = useDispatch();
	const currentTheme = useSelector(getCurrentTheme, shallowEqual);

	const ClearCacheComponent = withClearCache(() => {
		return <></>;
	});

	const { auth } = useSelector(isUserAuthorized) || {};

	const fetchInitialData = useCallback(() => {
		dispatch(fetchUserProfile());
		dispatch(loadInitialData());
	}, [dispatch]);

	const checkPersistency = useCallback(() => {
		dispatch(checkToken());
	}, [dispatch]);

	useEffect(() => {
		auth ? fetchInitialData() : checkPersistency();
	}, [auth, fetchInitialData, checkPersistency]);

	useEffect(() => {
		const refreshTheme = async () => {
			const theme = await getCookie(THEME);
			if (theme === undefined || theme === null) {
				await setCookie(THEME, true);
				await dispatch({ type: THEME, payload: true });
			} else {
				await dispatch({ type: THEME, payload: theme });
			}
		};
		refreshTheme();
	}, [dispatch]);

	useEffect(() => {
		document.documentElement.setAttribute("data-theme", currentTheme ? "light" : "dark");
	}, [currentTheme]);

	return (
		<Suspense>
			<ConfigProvider
				locale={es_ES}
				theme={{
					token: {
						borderRadius: 12,
						colorPrimary: "#6366f1",
						colorBgBase: currentTheme ? "#f8fafc" : "#0f172a",
						colorTextDisabled: currentTheme ? "rgba(0, 0, 0, 0.40)" : "rgba(255, 255, 255, 0.40)",
						fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
						colorSuccess: "#10b981",
						colorWarning: "#f59e0b",
						colorError: "#ef4444",
						colorInfo: "#3b82f6",
						colorLink: "#6366f1",
						controlHeight: 40,
					},
					components: {
						Switch: {
							colorPrimary: "#6366f1",
							colorPrimaryHover: "#818cf8",
						},
						Button: {
							borderRadius: 10,
							controlHeight: 40,
						},
						Input: {
							borderRadius: 10,
							controlHeight: 40,
						},
						Select: {
							borderRadius: 10,
						},
						Modal: {
							borderRadiusLG: 16,
						},
						Card: {
							borderRadiusLG: 16,
						},
					},
					algorithm: currentTheme ? theme.defaultAlgorithm : theme.darkAlgorithm,
				}}
			>
				<ClearCacheComponent />
				{auth ? <MainContainer /> : <AuthContainer />}
			</ConfigProvider>
		</Suspense>
	);
};

export default App;
